import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { tap } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { AmlService } from 'src/app/core/services/aml.service';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { ListActions } from 'src/app/core/state/list/list.actions';
import { ClientListType } from 'src/app/core/state/types';
import { AmlEntityType } from 'src/app/core/state/types/aml.types';
import { BlFrontendButtonComponent } from 'src/app/core/wrappers/bl-frontend-button.component';
import { FirmSettingsDialog } from 'src/app/firm-settings/firm.settings.dialog';
import { openTranslatableDialog } from 'src/app/shared/misc/openDialog';
import { TodayDate } from 'src/app/shared/misc/today.date';

@Component({
  selector: 'app-aml-button',
  template: `
    <ng-container *transloco="let t; prefix: this.scope">
      <ng-container *ngIf="hasActiveAmlConnection() === true; else noConnection">
        @if (hasAmlEntity()) {
          <bl-frontend-button
            density="slim"
            variant="secondary"
            [text]="toLocalDateFormat(latestAmlEntity().createdAt)"
            [trailingIcon]="{ name: 'arrow-up-right-from-square' }"
            (onclick)="openExistingAml(latestAmlEntity())"></bl-frontend-button>
        } @else {
          <bl-frontend-button
            density="slim"
            variant="tonal"
            [text]="t('createAmlControl')"
            [trailingIcon]="{ name: 'arrow-up-right-from-square' }"
            (onclick)="createAml(client)"></bl-frontend-button>
        }
      </ng-container>

      <ng-template #noConnection>
        <bl-frontend-button
          density="slim"
          [text]="t('getStarted')"
          variant="tonal"
          (onclick)="openSettingsDialog()"></bl-frontend-button>
      </ng-template>
    </ng-container>
  `,
  standalone: true,
  imports: [CommonModule, BlFrontendButtonComponent, TranslocoModule],
  providers: [getTranslationProvider('AmlButtonComponent')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AmlButtonComponent extends TranslatableComponent {
  @Input()
  get client(): ClientListType | null {
    return this.internalClient;
  }
  set client(client: ClientListType | null) {
    this.internalClient = client;
    this.latestAmlEntity.set(this.pickLatestAmlEntity(client?.amlEntities));
  }

  hasActiveAmlConnection = toSignal(this.store.select(FirmSelectors.selectHasActiveAmlConnection));
  latestAmlEntity = signal<AmlEntityType | null>(null);
  hasAmlEntity = computed(() => !!this.latestAmlEntity());

  private amlService = inject(AmlService);
  private dialogService = inject(DialogService);
  private pendingCreateAml = false;
  private internalClient: ClientListType | null = null;

  toLocalDateFormat(date: string): string {
    return date ? new TodayDate(this.getActiveLanguage()).formatDateByLocale(new Date(date), false) : '';
  }

  openExistingAml(amlEntity: AmlEntityType) {
    window.open(amlEntity.link, '_blank');
  }

  openSettingsDialog() {
    openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: FirmSettingsDialog,
      data: { tabIdToOpen: 'tabFirmSystemSettings' },
    });
  }

  createAml(client: ClientListType) {
    if (this.pendingCreateAml) {
      return;
    }

    this.pendingCreateAml = true;
    this.amlService
      .createAmlForClient(client.id)
      .pipe(
        tap((response: AmlEntityType) => {
          this.store.dispatch(
            ListActions.updateItemInListSucceeded({
              item: { ...client, amlEntities: [...(client.amlEntities || []), response] },
              predicate: (c: ClientListType) => c.id === client.id,
            }),
          );
        }),
        tap((response: AmlEntityType) => this.openExistingAml(response)),
      )
      .subscribe({
        next: () => {
          this.pendingCreateAml = false;
        },
        error: () => {
          this.pendingCreateAml = false;
        },
      });
  }

  private pickLatestAmlEntity(amlEntities: AmlEntityType[] | undefined): AmlEntityType | null {
    if (!amlEntities || amlEntities.length === 0) {
      return null;
    }

    return amlEntities.reduce((latest, current) => {
      return latest.createdAt > current.createdAt ? latest : current;
    });
  }
}
